import { Button } from '@/components/ui/button';

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader } from '@googlemaps/js-api-loader';
import { zodResolver } from '@hookform/resolvers/zod';
import { useStore } from '@nanostores/react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2'; // Import the PhoneInput component
import 'react-phone-input-2/lib/style.css'; // PhoneInput CSS
import { toast } from 'sonner';
import { z } from 'zod';

const formSchema = z
  .object({
    Name: z.string().min(2, {
      message: 'Account Receivable name must be at least 2 characters.',
    }),
    loanId: z.string(),
    description: z.string().nullable().optional(),
    portfolioId: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    middleName: z.string().nullable().optional(),
    contactEmail: z.string().email({ message: 'Invalid email address.' }),
    contactPhone: z.string(),
    customerType: z.enum(['Individual', 'Business', 'Organization']), // Adjust enum values as needed
    balance: z.string(),
    invoiceDate: z.date(),
    dueDate: z.date(),
    amountPaid: z.string(),
    amountDue: z.string(),
    streetAddress1: z.string(),
    streetAddress2: z.string(),
    city: z.string(),
    state: z.string(),
    postalCode: z.string(),
    country: z.string(),
    status: z.enum(['Unpaid', 'PartiallyPaid', 'Paid', 'Overdue', 'Cancelled', 'VoluntaryChurned', 'InvoluntaryChurned', 'VoluntaryChurnedRecovered', 'InvoluntaryChurnedRecovered']),
    quickBooksInvoiceLink: z.string().nullable().optional(),
    stripeInvoiceLink: z.string().nullable().optional(),
    customInvoiceLink: z.string().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.dueDate <= data.invoiceDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Due date must be later than the invoice date.',
        path: ['dueDate'], // Highlight the dueDate field for the error
      });
    }
  });

function parseAddressComponents(
  addressComponents: google.maps.GeocoderAddressComponent[],
) {
  const address = {
    streetAddress1: '',
    streetAddress2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
  };

  addressComponents.forEach(component => {
    const type = component.types[0];
    switch (type) {
      case 'street_number':
        address.streetAddress1 =
          `${component.long_name} ${address.streetAddress1}`.trim();
        break;
      case 'route':
        address.streetAddress1 =
          `${address.streetAddress1} ${component.long_name}`.trim();
        break;
      case 'subpremise':
        // e.g. apartment / suite #
        address.streetAddress2 = component.long_name;
        break;
      case 'locality':
        address.city = component.long_name;
        break;
      case 'administrative_area_level_1':
        address.state = component.short_name; // or component.long_name
        break;
      case 'country':
        address.country = component.long_name;
        break;
      case 'postal_code':
        address.postalCode = component.long_name;
        break;
      default:
        break;
    }
  });

  return address;
}

interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export function NewAccountReceivableDrawer({ open, setOpen }: Props) {
  const queryClient = useQueryClient();
  const org = useStore($org);
  const po = useStore($portfolio);
  const [customerType, setCustomerType] = useState<
    'Individual' | 'Business' | 'Organization'
  >('Individual'); //
  const [status, setStatus] = useState<
    'Unpaid' | 'PartiallyPaid' | 'Paid' | 'Overdue' | 'Cancelled' | 'VoluntaryChurned' | 'InvoluntaryChurned' | 'VoluntaryChurnedRecovered' | 'InvoluntaryChurnedRecovered'
  >('Unpaid'); //

  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      Name: '',
      loanId: '',
      description: null,
      portfolioId: '',
      firstName: '',
      lastName: '',
      middleName: '',
      contactEmail: '',
      contactPhone: '',
      customerType: 'Individual',
      balance: '0',
      invoiceDate: new Date(),
      dueDate: new Date(),
      amountPaid: '0',
      amountDue: '0',
      streetAddress1: '',
      streetAddress2: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      status: 'Unpaid',
      quickBooksInvoiceLink: '',
      stripeInvoiceLink: '',
      customInvoiceLink: '',
    },
  });

  // 2. Define a submit handler.
  const [isLoading, setIsLoading] = useState(false);
  // 1) Reference for the "Street Address 1" input
  // ------------- Google Places -------------
  const [autoService, setAutoService] =
    useState<google.maps.places.AutocompleteService | null>(null);
  const [placesService, setPlacesService] =
    useState<google.maps.places.PlacesService | null>(null);

  // We store the list of predictions in local state
  const [suggestions, setSuggestions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);

  // 1) Load the script if not present, then init services
  // Initialize the Google Maps Loader
  const googleMapsLoader = new Loader({
    apiKey: import.meta.env.VITE_GOOGLE_MAPS_ADDRESS_API_KEY,
    libraries: ['places'], // Include the Places library
  });

  const placesServiceRef = useRef<HTMLDivElement>(null);
  // Initialize Google Services
  const initServices = useCallback(async () => {
    if (autoService && placesService) return; // Already initialized

    try {
      const loader = new Loader({
        apiKey: import.meta.env.VITE_GOOGLE_MAPS_ADDRESS_API_KEY,
        libraries: ['places'],
      });
      await loader.load();

      const newAutoService = new google.maps.places.AutocompleteService();
      setAutoService(newAutoService);

      if (placesServiceRef.current) {
        const newPlacesService = new google.maps.places.PlacesService(
          placesServiceRef.current,
        );
        setPlacesService(newPlacesService);
      } else {
        throw new Error('PlacesService ref is not attached to a DOM element.');
      }
    } catch (error) {
      console.error('Failed to load Google Maps API:', error);
      toast.error(
        'Failed to load address autocomplete. Please try again later.',
      );
    }
  }, [autoService, placesService]);

  // Fetch Predictions Dynamically
  const fetchPlacePredictions = async (value: string) => {
    if (!autoService || !placesService) {
      await initServices(); // Ensure services are initialized
    }

    if (autoService) {
      autoService.getPlacePredictions(
        {
          input: value,
          componentRestrictions: { country: 'us' },
          types: ['address'], // Restrict to addresses
        },
        (predictions, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            predictions?.length
          ) {
            setSuggestions(predictions);
          } else {
            setSuggestions([]);
          }
        },
      );
    }
  };

  // Handle Address Input Change
  const handleAddressChange = async (value: string) => {
    form.setValue('streetAddress1', value);

    // If input is empty, clear suggestions
    if (!value.trim()) {
      setSuggestions([]);
      return;
    }

    await fetchPlacePredictions(value);
  };

  // 4) When user clicks a suggestion, get details, parse them
  const handleSelectSuggestion = (placeId: string, description: string) => {
    // Immediately hide suggestions
    setSuggestions([]);

    // Temporarily set the input's text to the suggestion
    form.setValue('streetAddress1', description);

    if (!placesService) return;
    placesService.getDetails(
      { placeId, fields: ['address_components'] },
      (place, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          place
        ) {
          if (place.address_components) {
            const parsed = parseAddressComponents(place.address_components);
            // Update form fields
            form.setValue('streetAddress1', parsed.streetAddress1);
            form.setValue('streetAddress2', parsed.streetAddress2);
            form.setValue('city', parsed.city);
            form.setValue('state', parsed.state);
            form.setValue('postalCode', parsed.postalCode);
            form.setValue('country', parsed.country);
          }
        }
      },
    );
  };

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      setIsLoading(true);
      const result = await createAccountReceivable(
        values.Name,
        po?.portfolio.id ?? '',
        values.loanId,
        values.firstName,
        values.lastName,
        values.middleName ?? '',
        values.contactEmail,
        `+${values.contactPhone}`,
        values.invoiceDate.toISOString(),
        values.dueDate.toISOString(),
        Number(values.amountPaid),
        Number(values.amountDue),
        values.customerType,
        Number(values.balance),
        values.streetAddress1,
        values.streetAddress2,
        values.city,
        values.state,
        values.postalCode,
        values.country,
        values.status,
        values.quickBooksInvoiceLink ?? '',
        values.stripeInvoiceLink ?? '',
        values.customInvoiceLink ?? '',
      );

      if (result?.succeeded) {
        toast.success('Account Receivable created successfully.');
        setOpen(false);
      } else {
        toast.error(
          'Error creating new account receivable: ' + result?.message,
        );
      }
    } catch (error) {
      toast.error('An error occurred while creating the account receivable.');
    } finally {
      setIsLoading(false);
    }
  }

  // Inside your component:
  const balance = form.watch("balance");
  const amountPaid = form.watch("amountPaid");

  useEffect(() => {
    const b = parseFloat(balance) || 0;
    const p = parseFloat(amountPaid) || 0;
    const calculatedDue = b - p;
    form.setValue("amountDue", calculatedDue.toString());
  }, [balance, amountPaid, form]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="overflow-y-auto sm:max-w-[600px]">
        <div ref={placesServiceRef} style={{ display: 'none' }} />
        <SheetHeader>
          <SheetTitle>Create new Account Receivable</SheetTitle>
          <SheetDescription>
            Fill in the details for the new account receivable.
          </SheetDescription>
        </SheetHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mt-4 space-y-8"
          >
            <FormField
              control={form.control}
              name="Name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account Receivable Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Account Receivable ABC" {...field} />
                  </FormControl>
                  <FormDescription>
                    Enter a Account Receivable name to help identify this file.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Tabs
              defaultValue={customerType}
              onValueChange={value =>
                form.setValue(
                  'customerType',
                  value as 'Individual' | 'Business' | 'Organization',
                )
              }
            >
              <TabsList>
                <TabsTrigger value="Individual">Individual</TabsTrigger>
                <TabsTrigger value="Business">Business</TabsTrigger>
                <TabsTrigger value="Organization">Organization</TabsTrigger>
              </TabsList>
            </Tabs>

            <Tabs
              defaultValue={status}
              onValueChange={value =>
                form.setValue(
                  'status',
                  value as
                  | 'Unpaid'
                  | 'PartiallyPaid'
                  | 'Paid'
                  | 'Overdue'
                  | 'Cancelled'
                  | 'VoluntaryChurned'
                  | 'InvoluntaryChurned'
                  | 'VoluntaryChurnedRecovered'
                  | 'InvoluntaryChurnedRecovered',
                )
              }
            >
              <TabsList>
                <TabsTrigger value="Unpaid">Unpaid</TabsTrigger>
                <TabsTrigger value="PartiallyPaid">Partially Paid</TabsTrigger>
                <TabsTrigger value="Paid">Paid</TabsTrigger>
                <TabsTrigger value="Overdue">Overdue</TabsTrigger>
                <TabsTrigger value="Cancelled">Cancelled</TabsTrigger>
                <TabsTrigger value="VoluntaryChurned">Voluntary Churned</TabsTrigger>
                <TabsTrigger value="InvoluntaryChurned">Involuntary Churned</TabsTrigger>
                <TabsTrigger value="VoluntaryChurnedRecovered">Voluntary Churned Recovered</TabsTrigger>
                <TabsTrigger value="InvoluntaryChurnedRecovered">Involuntary Churned Recovered</TabsTrigger>
              </TabsList>
            </Tabs>

            <FormField
              control={form.control}
              name="loanId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Invoice ID</FormLabel>
                  <FormControl>
                    <Input placeholder="Invoice ID" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="quickBooksInvoiceLink"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Quickbooks Payment Link</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Quickbooks Payment Link"
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="stripeInvoiceLink"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Stripe Payment Link</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Stripe Payment Link"
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="customInvoiceLink"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Custom Payment Link</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Custom Payment Link"
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Personal Information Fields */}
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <Input placeholder="First Name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Last Name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="middleName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Middle Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Middle Name"
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="contactEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contact Email</FormLabel>
                  <FormControl>
                    <Input placeholder="Contact Email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="contactPhone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contact Phone</FormLabel>
                  <FormControl>
                    <PhoneInput
                      country="us"
                      value={field.value}
                      onChange={value => {
                        field.onChange(value);
                      }}
                      inputProps={{
                        name: 'contactPhone',
                        required: true,
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Address Fields */}
            <FormField
              control={form.control}
              name="streetAddress1"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Street Address 1</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Street Address 1"
                      onChange={e => handleAddressChange(e.target.value)}
                    />
                  </FormControl>
                  {suggestions.length > 0 && (
                    <ul className="mt-2 max-h-48 overflow-auto rounded-md border bg-white p-2">
                      {suggestions.map(suggestion => (
                        <li
                          key={suggestion.place_id}
                          className="cursor-pointer p-2 hover:bg-gray-100"
                          onClick={() =>
                            handleSelectSuggestion(
                              suggestion.place_id,
                              suggestion.description,
                            )
                          }
                        >
                          {suggestion.description}
                        </li>
                      ))}
                    </ul>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="streetAddress2"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Street Address 2</FormLabel>
                  <FormControl>
                    <Input placeholder="Street Address 2" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>City</FormLabel>
                  <FormControl>
                    <Input placeholder="City" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="state"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>State</FormLabel>
                  <FormControl>
                    <Input placeholder="State" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="postalCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Postal Code</FormLabel>
                  <FormControl>
                    <Input placeholder="Postal Code" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Country</FormLabel>
                  <FormControl>
                    <Input placeholder="Country" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Date and Amount Fields */}
            <FormField
              control={form.control}
              name="invoiceDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Invoice Date</FormLabel>
                  <br />
                  <FormControl>
                    <Input
                      type="date" // Use the native HTML date picker
                      value={
                        field.value
                          ? field.value.toISOString().split('T')[0]
                          : ''
                      } // Format the date for the input
                      onChange={e => {
                        const value = e.target.value;
                        field.onChange(value ? new Date(value) : null); // Convert to Date object
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="dueDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Due Date</FormLabel>
                  <br />
                  <FormControl>
                    <Input
                      type="date" // Use the native HTML date picker
                      value={
                        field.value
                          ? field.value.toISOString().split('T')[0]
                          : ''
                      } // Format the date for the input
                      onChange={e => {
                        const value = e.target.value;
                        field.onChange(value ? new Date(value) : null); // Convert to Date object
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="balance"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Balance</FormLabel>
                  <FormControl>
                    <Input
                      type="Number"
                      placeholder="Balance"
                      value={field.value}
                      onChange={e => {
                        const value = e.target.value;
                        if (/^\d*\.?\d*$/.test(value)) {
                          field.onChange(value);
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="amountPaid"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount Paid</FormLabel>
                  <FormControl>
                    <Input
                      type="Number"
                      placeholder="Amount Paid"
                      value={field.value}
                      onChange={e => {
                        const value = e.target.value;
                        if (/^\d*\.?\d*$/.test(value)) {
                          field.onChange(value);
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="amountDue"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount Due</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="Amount Due"
                      value={field.value}
                      readOnly
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />



            <SheetFooter>
              <SheetClose asChild>
                <Button variant="outline">Cancel</Button>
              </SheetClose>
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Submit
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
